/**
 * draftLabels — THE reading of a drafted document's identity.
 *
 * A draft carries two strings that look like names and only one of them is:
 *
 *   `name`              what a person called it. May be null.
 *   `document_type_id`  a SLUG, and for a user's own structure template a
 *                       SYNTHETIC slug: `template_<uuid>` (see
 *                       `wizard/ChooseTemplateStep.selectOwn`, which falls
 *                       back to that shape whenever the template declares no
 *                       DraftType).
 *
 * The page used to render `document_type_id.replaceAll('_', ' ')` wherever a
 * name was missing — in the title, in the meta line's type chip, and (via
 * `activityModel.humanizeDocType`) on every activity row. For a template-based
 * draft that prints `template 3f2a1b4c-9d8e-…`: a raw UUID, in the largest
 * text on the page, as the document's name.
 *
 * So the slug is not a fallback name. It is an id that is SOMETIMES also a
 * readable label, and the difference has to be decided before it reaches a
 * user. That decision lives here, once, and every drafting surface reads it
 * from here rather than re-deriving it.
 */

import { isOpaqueId } from '../../utils/identifiers';

const titleize = (s: string): string =>
  s.replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * The readable half of a `document_type_id`, or '' when there isn't one.
 *
 * `contracts_purchase_agreement` → `Contracts Purchase Agreement`
 * `template_3f2a1b4c-9d8e-…`     → ''   (nothing here is for a human)
 * `template_nda`                 → `Nda`
 *
 * Returning '' rather than a mangled id is the point: a caller that gets ''
 * knows it must reach for a real fallback instead of printing this.
 */
export const humanDocType = (documentTypeId?: string | null): string => {
  const raw = (documentTypeId || '').trim();
  if (!raw) return '';
  if (isOpaqueId(raw)) return '';

  const parts = raw.split('_').filter(Boolean);
  // Drop the synthetic `template_` prefix — it names the STORAGE of the
  // structure, not the kind of document, and the user never chose the word.
  const body = parts[0]?.toLowerCase() === 'template' ? parts.slice(1) : parts;
  const words = body.filter((p) => !isOpaqueId(p));
  if (words.length === 0) return '';
  return titleize(words.join(' '));
};

/** What a drafted document is called, for any surface that shows one. */
export interface DraftLike {
  name?: string | null;
  document_type_id?: string | null;
  kind?: string | null;
}

/**
 * THE title of a drafted document.
 *
 * Name first, because that is the only string a person authored. Then the
 * readable document type. Then — never an id — a friendly placeholder, which
 * is also the signal to the reader that this document still needs naming.
 *
 * `untitled` is passed in already translated so this file stays pure and
 * i18n-free; every call site already holds `t`.
 */
export const draftTitle = (
  item: DraftLike | null | undefined,
  untitled: string,
): string => {
  const named = (item?.name || '').trim();
  if (named) return named;
  const typed = humanDocType(item?.document_type_id);
  if (typed) return typed;
  return untitled;
};

/**
 * The type chip under the title — '' when it would merely repeat the title,
 * and '' when the id is opaque. Never an id.
 */
export const draftTypeLabel = (
  item: DraftLike | null | undefined,
  title: string,
  category?: string | null,
): string => {
  const typed = humanDocType(item?.document_type_id);
  const cat = (category || '').trim();
  if (!typed || typed === title) return cat;
  return cat ? `${typed} · ${cat}` : typed;
};

/** A filesystem-friendly stem for downloads — never a uuid. */
export const draftFilename = (
  item: DraftLike | null | undefined,
  untitled: string,
): string => draftTitle(item, untitled);

export default draftTitle;
